import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconForms, IconPlus } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { trpc } from '../../lib/trpc';

/**
 * Forms (WhatsApp Flows) — Meta in-chat forms: lead capture, bookings,
 * feedback. Not to be confused with bot flows (the automation builder).
 */

function statusColor(status: string): string {
  if (status === 'PUBLISHED') return 'green';
  if (status === 'DEPRECATED') return 'gray';
  if (status === 'DRAFT') return 'yellow';
  return 'blue'; // local draft, not yet pushed
}

function FormsIndex() {
  const navigate = useNavigate();
  const list = trpc.forms.list.useQuery();
  const presets = trpc.forms.presets.useQuery();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [presetKey, setPresetKey] = useState<string | null>('lead_capture');

  const create = trpc.forms.create.useMutation({
    onSuccess: (r) => {
      setCreating(false);
      void navigate({ to: '/vendor/forms/$uid', params: { uid: r.uid } });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  if (list.isPending) return <Loader />;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconForms size={22} />
            <Title order={2}>Forms</Title>
          </Group>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            New form
          </Button>
        </Group>
        <Text c="dimmed" size="sm">
          Native WhatsApp forms (Meta Flows): capture leads, take booking requests, and collect
          feedback without the customer ever leaving the chat. Forms need Meta approval-free
          publishing — push, publish, send.
        </Text>

        {(list.data ?? []).length === 0 && (
          <Card withBorder>
            <Text c="dimmed" size="sm">
              No forms yet — create one from a preset to get going in under a minute.
            </Text>
          </Card>
        )}

        <Stack gap="xs">
          {(list.data ?? []).map((f) => (
            <Card
              key={f.uid}
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => void navigate({ to: '/vendor/forms/$uid', params: { uid: f.uid } })}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text fw={600}>{f.name}</Text>
                    <Badge color={statusColor(f.status)} variant="light">
                      {f.status === 'draft' ? 'Not pushed' : f.status}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    CTA &quot;{f.ctaText}&quot; · {f.responseCount} response
                    {f.responseCount === 1 ? '' : 's'}
                  </Text>
                </Stack>
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>

      <Modal opened={creating} onClose={() => setCreating(false)} title="New form">
        <Stack gap="sm">
          <TextInput
            label="Form name"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Select
            label="Start from"
            data={[
              ...(presets.data ?? []).map((p) => ({
                value: p.key,
                label: `${p.name} — ${p.description}`,
              })),
            ]}
            value={presetKey}
            onChange={setPresetKey}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={!name.trim()}
              onClick={() =>
                create.mutate({ name: name.trim(), presetKey: presetKey ?? undefined })
              }
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/forms/')({ component: FormsIndex });
